<script setup lang="ts">
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import DropdownCheckbox from '@/components/Globals/Inputs/DropdownCheckbox.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'

import { formatPeriodDate } from '@/utils/date/date-time'
import { translateAlarmType } from '@/interfaces/enums'

import { IAlarmReportSearchBarSchema } from '@/interfaces/api/schemas/alarm'
import { IGroup } from '@/interfaces/entities/group'

import AlarmReportSearchBarSchema from '@/schemas/alarm/AlarmReportSearchBarSchema'

import { useAlarmStore } from '@/store/alarm'
import { useCompanyStore } from '@/store/company'
import { useGroupStore } from '@/store/group'

import { useSensorStore } from '@/store/sensor'
import { storeToRefs } from 'pinia'
import { Form, Field } from 'vee-validate'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const isLoading = ref(false)
const formRef = ref<{ setFieldValue: (name: string, value: unknown) => void } | null>(null)
const alarmStore = useAlarmStore()
const {
  fetchAlarmReportMetrics,
  setCurrentGroupFilter,
  setReportSensorIds,
  setReportAlarmTypeKeys,
  setReportClassificationTypeIds,
  setCurrentShiftFilter,
  setLastPeriodReport,
} = alarmStore
const { listSensors } = storeToRefs(useSensorStore())
const { listClassifications } = storeToRefs(alarmStore)
const {
  getCurrentGroupFilter,
  getReportSensorIds,
  getReportAlarmTypeKeys,
  getReportClassificationTypeIds,
  getLastSearchAlarmsType,
  getLastPeriodReportFilter,
  getCurrentShiftFilter,
} = storeToRefs(alarmStore)
const { fetch: fetchSensors } = useSensorStore()
const { fetchAlarmClassifications } = alarmStore

const groupStore = useGroupStore()
const { listGroups } = storeToRefs(groupStore)
const { fetch: fetchGroups } = groupStore

const previewGroupForSensors = ref<'unset' | IGroup | null>('unset')

const { fetchCompanyShifts, fetchCompanyAlarmTypes } = useCompanyStore()
const { shifts, currentCompany, hasShifts, getAlarmTypeConfigs } = storeToRefs(useCompanyStore())

function activeGroupForSensors(): IGroup | null {
  if (previewGroupForSensors.value === 'unset') return getCurrentGroupFilter.value
  return previewGroupForSensors.value
}

const sensorsForReport = computed(() => {
  const group = activeGroupForSensors()
  const all = listSensors.value
  if (!group?.sensors?.length) return all
  const ids = new Set(group.sensors.map((s) => s.id))
  return all.filter((s) => ids.has(s.id))
})

const sensorDropdownRemountKey = ref(0)

const groupDropdownKey = computed(() => String(getCurrentGroupFilter.value?.id ?? 'g'))

const sensorDropdownKey = computed(() => String(sensorDropdownRemountKey.value))

const formInitialValues = computed(() => ({
  group: getCurrentGroupFilter.value ?? null,
  sensor_ids: [...getReportSensorIds.value],
  alarm_types: [...getReportAlarmTypeKeys.value],
  classification_type_ids: [...getReportClassificationTypeIds.value],
  shift: getCurrentShiftFilter.value ?? null,
}))

function isGroupAllOption(option: object | null | undefined): boolean {
  if (option == null) return true
  const o = option as { id?: number | null }
  return o.id === null || o.id === undefined
}

function onGroupSelected(option: object) {
  if (isGroupAllOption(option)) {
    previewGroupForSensors.value = null
    return
  }
  previewGroupForSensors.value = option as IGroup
  formRef.value?.setFieldValue('sensor_ids', [])
  sensorDropdownRemountKey.value++
}

const onSubmit = (values: object) => {
  isLoading.value = true

  const castedValues = values as IAlarmReportSearchBarSchema

  const formattedPeriodDate = formatPeriodDate(castedValues.period)
  const isClassifiedView = getLastSearchAlarmsType.value === 'classified'

  setCurrentGroupFilter(castedValues.group ?? null)
  setReportSensorIds(castedValues.sensor_ids ?? [])
  setReportAlarmTypeKeys(castedValues.alarm_types ?? [])
  setReportClassificationTypeIds(isClassifiedView ? (castedValues.classification_type_ids ?? []) : [])
  setCurrentShiftFilter(castedValues.shift ?? null)
  setLastPeriodReport(castedValues.period)

  fetchAlarmReportMetrics(formattedPeriodDate).finally(() => {
    isLoading.value = false
  })
}

const handleShiftSelected = (_shift: object) => {
  void _shift
}

const listAlarmTypes = computed(() =>
  getAlarmTypeConfigs.value.map((c) => ({
    id: c.alarm_type,
    name: translateAlarmType(c.alarm_type),
  }))
)

onMounted(() => {
  fetchSensors()
  fetchCompanyShifts()
  fetchCompanyAlarmTypes()
  fetchAlarmClassifications()
  fetchGroups()
})

watch(currentCompany, (newValue) => {
  if (newValue) {
    fetchCompanyShifts()
    fetchCompanyAlarmTypes()
    fetchGroups({ force: true })
  }
})
</script>

<template>
  <div class="mt-4 w-full bg-white p-4 shadow-md">
    <Form
      ref="formRef"
      class="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end"
      :initial-values="formInitialValues"
      @submit="onSubmit"
      :validation-schema="AlarmReportSearchBarSchema">
      <SearchBarField :label="t('alarm_report.header.buttons.period')" class="w-full min-w-0 lg:min-w-[300px] lg:max-w-[400px] lg:flex-1">
        <Field v-slot="{ field }" name="period">
          <NewDateRangePicker
            v-bind="field"
            :already-selected-period="getLastPeriodReportFilter"
            :placeholder="t('plugins.vue_datepicker.placeholder')"
            class="w-full" />
        </Field>
      </SearchBarField>

      <SearchBarField :label="t('alarm_report.header.buttons.group')" class="w-full min-w-0 lg:w-[220px] lg:shrink-0">
        <Dropdown
          :key="groupDropdownKey"
          name="group"
          :options="listGroups"
          label-attribute="name"
          :has-all-option="true"
          :already-selected-option="getCurrentGroupFilter"
          class="w-full"
          @option-selected="onGroupSelected" />
      </SearchBarField>

      <SearchBarField :label="t('alarm_report.header.buttons.sensor')" class="w-full min-w-0 lg:w-[220px] lg:shrink-0">
        <DropdownCheckbox
          :key="sensorDropdownKey"
          name="sensor_ids"
          :options="sensorsForReport"
          label-attribute="name"
          :has-all-option="true"
          :already-selected-option="getReportSensorIds"
          searchable
          class="w-full" />
      </SearchBarField>

      <SearchBarField :label="t('alarm_report.header.buttons.type')" class="w-full min-w-0 lg:w-[220px] lg:shrink-0">
        <DropdownCheckbox
          name="alarm_types"
          id-mode="string"
          :options="listAlarmTypes"
          label-attribute="name"
          :has-all-option="true"
          :already-selected-option="getReportAlarmTypeKeys"
          class="w-full" />
      </SearchBarField>

      <SearchBarField
        v-if="getLastSearchAlarmsType === 'classified'"
        :label="t('alarm_report.header.buttons.classification')"
        class="w-full min-w-0 lg:w-[220px] lg:shrink-0">
        <DropdownCheckbox
          v-if="getLastSearchAlarmsType === 'classified'"
          name="classification_type_ids"
          :options="listClassifications"
          label-attribute="name"
          :has-all-option="true"
          :already-selected-option="getReportClassificationTypeIds"
          class="w-full" />
      </SearchBarField>

      <SearchBarField
        v-if="hasShifts"
        :label="t('dashboard.header.buttons.shift')"
        class="w-full min-w-0 lg:w-[220px] lg:shrink-0">
        <Dropdown
          name="shift"
          :options="shifts"
          label-attribute="name"
          :has-all-option="true"
          :placeholder="t('placeholders.select_shift')"
          class="w-full"
          @option-selected="handleShiftSelected" />
      </SearchBarField>

      <div class="flex w-full lg:ml-auto lg:w-auto lg:shrink-0">
        <button
          :disabled="isLoading"
          type="submit"
          class="flex min-h-[46px] w-full min-w-[120px] flex-row items-center justify-center bg-blue px-4 py-3 text-sm text-white hover:bg-blue-2 lg:w-auto">
          <IcSearch v-if="!isLoading" class="mr-2 h-4 w-4" />
          <span v-else class="mr-2 animate-spin">
            <IcAnimateSpin class="h-4 w-4 text-white" />
          </span>
          {{ t('alarm_report.header.buttons.search') }}
        </button>
      </div>
    </Form>
  </div>
</template>
